import { z } from "zod";
import { roomSummarySchema } from "./room-summary.js";
import { seatReservationSchema } from "./seat-reservation.js";
/**
 * The join flow's REST surface — phase plan **S4-5**, architecture **§3.3**.
 *
 * ── `POST /api/v1/rooms/join` ───────────────────────────────────────────────
 *
 * The client names a **tile**, never a room. Matchmaking is not the client's
 * (`exposedMethods: []`): the api resolves the config's current `filling` room
 * from Postgres at call time, reserves a seat in it and debits the entry fee in
 * one transaction.
 *
 * The response envelope is **LOCKED as `{ reservation, redirected }`** (§16 A11)
 * and `reservation` travels **verbatim** — see `seat-reservation.ts`. The route
 * that serves it attaches no Fastify response schema for exactly that reason, so
 * `joinRoomResponseSchema` below is documentation and a client-side shape, not a
 * serialiser.
 */
export const joinRoomRequestSchema = z.object({
    /** `room_configs.id` — the tile that was tapped. */
    roomConfigId: z.string().uuid(),
    /**
     * The room the client believed that tile pointed at, if it knows one.
     *
     * §3.3: the reserve call resolves the current filling room "and returns
     * `{redirected: true}` if it differs from what the client had". This optional
     * field is the only place a client *can* say what it had — and today nothing
     * sends it, because the lobby read model deliberately carries no room id on a
     * tile (`lobbyTileSchema`). It is accepted now so that the day a surface knows
     * its room (S4-7's "Play again" is the first), the contract does not move and
     * the redirect toast starts firing on the wider case without a server change.
     *
     * Either identifier is understood: ours (`rooms.id`) or Colyseus's.
     */
    roomId: z.string().min(1).max(64).optional(),
});
export const joinRoomResponseSchema = z.object({
    reservation: seatReservationSchema,
    /**
     * The seat is not in the room the caller asked for.
     *
     * Ivy §2 mount table: the screen mounts and **then** raises the redirect
     * toast. Required, never optional — A11: a client must not treat its absence
     * as `false` by accident.
     */
    redirected: z.boolean(),
});
/**
 * ── `POST /api/v1/rooms/:id/leave` ──────────────────────────────────────────
 *
 * Pre-game leave. Juno's matrix: **every exit before the first round starts is
 * refunded 100%**, so this route never computes a percentage — it negates the
 * recorded entry debit through `refundEntry()` (LOCKED rule 27) and reports what
 * came back.
 *
 * `refundedCoins` is a **positive** number and is 0 on a free room; `refunded`
 * is the branch a client should read, because "0 coins came back" and "nothing
 * was owed" are the same number and different statements.
 */
export const leaveRoomResponseSchema = z.object({
    /** Our `rooms.id`, whichever identifier the caller used in the path. */
    roomId: z.string(),
    /** The seat is closed. `false` is never returned — a failure is an error envelope. */
    left: z.boolean(),
    refunded: z.boolean(),
    refundedCoins: z.number().int().min(0),
    /** The caller's own balance after the refund. Their number, nobody else's. */
    balanceCoins: z.number().int().min(0),
});
/**
 * ── `POST /api/v1/rooms/:id/rejoin` — architecture §16f **A42** ─────────────
 *
 * The missing half of LOCKED rule 18. Rule 18 holds a dropped player's seat for
 * the whole grace window *so they can come back*; until this route existed they
 * could not, and the hold expired into a forfeit for a seat somebody had paid
 * for.
 *
 * **`:id` takes either identifier** (A41: any route reachable from the
 * `/room/:roomId` URL accepts both spaces — the same `findRoomByEitherId` the
 * leave route uses). **No body**: there is nothing a client may say about who it
 * is, and the room is named by the path.
 *
 * ── Money: none, ever (A42) ────────────────────────────────────────────────
 *
 * The route must not call `packages/ledger` **at all** — no debit, no refund, no
 * replay of `entry:{roomId}:{playerId}`. Holding a `joined` `room_participants`
 * row for that room **is** the authorisation; a caller without one gets a 4xx and
 * never a charge. The rule is enforced structurally rather than by review: the
 * service module is barred from importing `@wr/ledger` by an ESLint rule
 * (`eslint.config.mjs`, architecture rule 5).
 */
export const rejoinRoomResponseSchema = z.object({
    /**
     * Verbatim from the game service (§16 A8/A11). **The route attaches no
     * Fastify response schema**, exactly as `/rooms/join` does not: a compiled
     * serialiser drops unknown keys, and on this route one of the keys it would
     * drop is `reconnectionToken` — the field that makes the socket land in the
     * seat the player already has rather than in a new one.
     */
    reservation: seatReservationSchema,
    /** Ours (`rooms.id`), whichever identifier the caller used in the path. */
    roomId: z.string(),
    /** `room_configs.id`, so the room screen's `Play again` has its tile (§8.4). */
    roomConfigId: z.string(),
    /** The seat they already had. Never a new one (A42). */
    seatIndex: z.number().int().min(0),
});
/**
 * **The failure half, and A42 calls it "possibly the larger half".**
 *
 * A refused rejoin answers with the room's summary and this participant's own
 * two facts, so the client renders the **true terminal** — Ivy's
 * T1/T2/T3/T4/T5/T7 and, since §9.9, **T8** — instead of falling back to T6. T6
 * then means what it should: *we could not find out*, rather than *we did not
 * ask*.
 *
 * `room.phase` and `room.cancelReason` are `rooms.*` **as persisted**, not the
 * in-room vocabulary: the room object may be long gone by the time somebody
 * reloads. Turning them into a panel is the client's job (A43's rule — the wire
 * carries facts, the client derives the look).
 *
 * **The shape is `{ reason, room, seatOutcome, prizeCoins }` — A45's split, made
 * structural.** The room facts are the named, reusable half; the viewer's two
 * facts are siblings and may never move inside it.
 */
export const rejoinFailureDetailsSchema = z.object({
    reason: z.enum(["no_seat", "seat_reserved", "seat_ended", "room_over", "unavailable"]),
    /**
     * ── The room half, as the canonical `RoomSummary` (A45) ───────────────────
     *
     * Eight facts about the room and none about any player: the two ids, the two
     * display names, the two snapshot money values, the phase and the cancel
     * reason. It was eight loose fields until T-215; naming the shape is A45, and
     * **nesting it is what keeps the split honest** — `room.prizeCoins` is what
     * the room promised in total and `prizeCoins` below is what *this player* was
     * credited, which are different numbers that a flat body would have had to
     * spell differently to tell apart.
     *
     * Why the room half is here at all (T-214's documented extension to A42's
     * three fields): every panel A42 asks for carries money in its copy — T3
     * states the entry that was kept, T4 the refund, T1/T7 that nothing was lost —
     * and a cold reload has no snapshot to read those numbers from. The
     * alternatives were rendering the fee-0 wording at a 25-coin table (a lie
     * about coins) or rendering no body at all. The ids are the same story for
     * controls: `roomId` (`rooms.id`, A41's comparison space) is what the
     * spent-room set records so the lobby tile is true *before* the next tap, and
     * `roomConfigId` is what `Join again` re-joins — the config, never this room.
     *
     * Nothing in it is a presentation derivation (A43): they are facts, and the
     * client decides what they look like.
     */
    room: roomSummarySchema,
    /**
     * `released` (entry refunded, Ivy T4) | `forfeited` (entry kept, T3) | **`null`**
     * (this seat has not ended, or we cannot say how).
     *
     * **`null`, not `""` — T-217 (Quinn N3).** §19 line 28 routes *"outcome absent
     * or unrecognised on an ended seat ⇒ T6"* and §9.9 reads the same absence as
     * T8's only input on a `completed` room, so *absent* is a value two rules
     * branch on. It reached the wire as `""` because the Colyseus schema's
     * empty-string default was being serialised into an HTTP body — a third state
     * nobody specced, in a field whose whole job is to distinguish two.
     *
     * `null` on a **`completed`** room is not "unknown" — it is *this seat never
     * ended early*, and it is T8's only input (§9.9). On any other phase it is
     * T6, because T4 claims a refund and T3 claims a loss and a wrong guess lies
     * about coins in one direction or the other.
     *
     * **A sibling of `room`, never a member of it** (A45, LOCKED): it is a fact
     * about the viewer, and a summary that quietly contains one viewer's outcome
     * leaks silently the first time it is reused for a second audience.
     */
    seatOutcome: z.enum(["released", "forfeited"]).nullable(),
    /**
     * **`me.prizeCoins` — what this player was *credited* in this room** (Ivy §17
     * note 9, architecture A45 addendum). The viewer's second sibling.
     *
     * **Binding on its derivation (A45 addendum):** it is the credited
     * `room.prize` transaction for this (room, player) —
     * `room_participants.prize_txn_id` → `coin_transactions.amount_delta` — and it
     * is **never** computed from `prize_snapshot`, from `prize_distribution` or by
     * a client-side split. Rule 27's principle applied to the paying direction:
     * the number a player is shown is the number the ledger **paid**, not the
     * number the config **promised**, and the two differ exactly when something
     * went wrong. Reading it off the persisted rows is also what makes it survive
     * a cold rejoin, which is the only case it exists for.
     *
     * `null` means *the server cannot say* — no participant row, or a room that
     * has not settled — and on a **paid** room the client renders **T6** rather
     * than guessing (§9.9 LOCKED). It is **never rendered as `0`** (§8.3's no-`+0`
     * rule). `0` is a different statement: *this room settled and credited this
     * player nothing*, which is T8's `prize_other` branch.
     */
    prizeCoins: z.number().int().min(0).nullable(),
});
/**
 * `POST /internal/rooms/:roomId/seats/rejoin` on the game service (A42).
 *
 * A37 unchanged: the api **commands** ("readmit this participant") and the room
 * executes through its own lifecycle. The api never writes room state.
 */
export const rejoinSeatRequestSchema = z.object({
    /** `players.id` — the seat's owner, from the session. Never a session id. */
    playerId: z.string().uuid(),
});
/**
 * `POST /internal/rooms/:roomId/seats/release` on the game service (S4-5).
 *
 * The api's leave route is authoritative for the money and for the participant
 * row; this is how it tells the room to let go of the socket, so a client cannot
 * take its refund and stay seated. It is a remote room call under the hood, so
 * it works from whichever process answered.
 */
export const releaseSeatRequestSchema = z.object({
    /** `players.id` — the seat's occupant, not a session id. */
    playerId: z.string().uuid(),
    /** Logged. A seat released without a stated reason is not auditable. */
    reason: z.string().trim().min(1).max(120),
});
export const releaseSeatResponseSchema = z.object({
    /** `false` when the room had no live socket for that player — normal, not an error. */
    released: z.boolean(),
    /**
     * **The room let go of an unconsumed seat *reservation*** — T-219 (Quinn B2).
     *
     * A separate fact from `released`, and deliberately not folded into it: a
     * reservation is a seat held for a socket that has not arrived, so "we closed
     * a live socket" and "we cancelled a hold nobody consumed" are different
     * events and only the first is what `released` has always meant (T-217, Quinn
     * N2 pinned that wording). Both `false` means the room was holding nothing for
     * this player, which is still a `200`.
     *
     * Why it matters at all: until this existed a fully refunded pre-game leave
     * left its reservation counting towards `maxClients` for the whole
     * `seat_reservation_ms`, so two zero-cost join+leave cycles locked the paid
     * 1v1 tile and answered an innocent funded player `503`.
     */
    reservationCancelled: z.boolean(),
    roomId: z.string(),
});
//# sourceMappingURL=rooms.js.map